import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { InternalAxiosRequestConfig } from 'axios';
import { getApiClient } from '@clarix/api-client';
import {
  isNetworkFailure,
  isStorageUnreachable,
  NETWORK_MESSAGE,
  SAVE_REFUSED_MESSAGE,
  UPLOAD_UNREACHABLE_MESSAGE,
  type MicSwapEvent,
  type UseRecordingControlsReturn,
} from '@clarix/meeting-recording';
import type { ClaraRecordingStatus } from '@clarix/shared-types';
import { toast } from '@clarix/ui';
import { isConnectivityLost, subscribeConnectivity } from '@clarix/utils';
import { quickMeetingApi } from './api';
import {
  abortWithRetry,
  completeWithFallback,
  createCalendarRecordingAdapter,
  isEmptyStop,
  NO_AUDIO_CODE,
  reopenUpload,
  type CalendarRecordingAdapter,
  type CompleteOutcome,
} from './adapter';
import {
  acquireRecordingLock,
  clearLocalParts,
  drainLocalParts,
  playChime,
  primeChime,
  probeMicrophone,
  downloadLocalAudio,
  requeueLocalParts,
  restartLocalUpload,
  savedMic,
  sidecars,
  uuid,
  type SessionSidecar,
} from './browser';
import { QM_LIMITS, QM_RECOVERY, QM_TIMEOUTS, quickMeetingScope } from './constants';
import {
  ANOTHER_TAB_START,
  MIC_NO_DEVICE,
  MIC_UNSUPPORTED,
  QM_COPY,
  START_ERROR_COPY,
} from './copy';
import { errorCode, startErrorMessage } from './format';
import type { CreateQuickMeetingResponse, QuickMeetingUnavailableReason } from './types';
import { useCrossTabRecording, type RemoteRecording } from './useCrossTabRecording';
import type { EngineMic } from './useQuickMeetingMic';

export type LocalPhase =
  | 'idle'
  | 'requesting_mic'
  | 'denied'
  | 'starting'
  | 'recording'
  | 'paused'
  | 'saving'
  | 'save_failed'
  /** `complete` hit RECORDING_PARTS_MISMATCH: only Discard is left. */
  | 'no_audio';

/** This tab's recording. Times are `Date.now()` ms; `accumulatedMs` excludes pauses. */
export interface LocalRecording {
  phase: LocalPhase;
  recordingId: string | null;
  eventId: string | null;
  stopAt: number | null;
  warnAt: number | null;
  accumulatedMs: number;
  runningSince: number | null;
  warned: boolean;
  error: string | null;
  /** Set when the take was paused for us: the computer slept and nothing was captured. */
  pauseReason: 'asleep' | null;
  /** Saving waits for the network; it retries on its own once the browser is back online. */
  awaitingNetwork: boolean;
  /** That wait is S3 not answering while the internet works: "Couldn't upload", not "Offline". */
  uploadStuck: boolean;
  /** The take this tab just finished, so its last `active` row never reads as orphaned. */
  endedId: string | null;
}

export interface EngineMount {
  key: string;
  adapter: CalendarRecordingAdapter;
  dbName: string;
}

export interface StartInput {
  title: string;
}

interface Session extends SessionSidecar {
  /** `client_session_id`: the engine's scope id and the RecordingProvider key. */
  key: string;
  adapter: CalendarRecordingAdapter;
}

type StopReason = 'user' | 'auto' | 'remote' | 'signout';

const SLEEP_TOAST_ID = 'quick-meeting-asleep';

/** The engine's error text for a stop that waits rather than fails. */
function engineWaitReason(error: string | null): WaitReason | null {
  if (error === UPLOAD_UNREACHABLE_MESSAGE) return 'unreachable';
  return error === NETWORK_MESSAGE ? 'offline' : null;
}

/** Saves a session's on-device audio as a file; says so when nothing is left. */
function downloadFor(dbName: string | null, session: SessionSidecar | null | undefined): (() => void) | undefined {
  if (!session || !dbName) return undefined;
  return () =>
    void downloadLocalAudio(dbName, session).then((saved) => {
      if (!saved) toast.info(QM_COPY.nothingOnDevice);
    });
}

const downloadAction = (download: (() => void) | undefined) =>
  download ? { label: QM_COPY.download, onClick: download } : undefined;

/** A fresh read of the row: its status, `'gone'` on a 404, or null when it couldn't be read. */
async function rowNow(slug: string, recordingId: string): Promise<string | null> {
  try {
    return (await quickMeetingApi.get(slug, recordingId)).status;
  } catch (err) {
    return errorCode(err).status === 404 ? 'gone' : null;
  }
}
/** Row states a heartbeat may still meet while this tab captures; anything else was saved elsewhere. */
const STILL_UPLOADING: ReadonlySet<string> = new Set(['recording', 'stale', 'upload_failed']);
const OFFLINE_TOAST_ID = 'quick-meeting-offline';

const IDLE: LocalRecording = {
  phase: 'idle',
  recordingId: null,
  eventId: null,
  stopAt: null,
  warnAt: null,
  accumulatedMs: 0,
  runningSince: null,
  warned: false,
  error: null,
  pauseReason: null,
  awaitingNetwork: false,
  uploadStuck: false,
  endedId: null,
};

export function recordedMsAt(l: LocalRecording, now: number): number {
  return l.accumulatedMs + (l.runningSince != null ? Math.max(0, now - l.runningSince) : 0);
}

export interface ClockSample {
  /** `Date.now()`. */
  wall: number;
  /** The recording graph's `currentTime` in seconds; it stops whenever capture stops. */
  audio: number | null;
}

/** Where capture froze between two samples (the computer slept), or null when it kept running. */
export function freezeStart(prev: ClockSample, next: ClockSample): number | null {
  const wallMs = next.wall - prev.wall;
  if (prev.audio != null && next.audio != null) {
    const audioMs = Math.max(0, (next.audio - prev.audio) * 1000);
    return wallMs - audioMs > QM_RECOVERY.SLEEP_GAP_MS ? prev.wall + audioMs : null;
  }
  return wallMs > QM_RECOVERY.SLEEP_TICK_GAP_MS ? prev.wall : null;
}

/**
 * The wall time the recording graph has rendered up to since `since`; null without an audio clock.
 * After a freeze Chrome can render slower than real time, so wall time alone counts audio never captured.
 */
export function capturedUntil(since: ClockSample, next: ClockSample): number | null {
  if (since.audio == null || next.audio == null || next.audio < since.audio) return null;
  return since.wall + Math.min(next.wall - since.wall, (next.audio - since.audio) * 1000);
}

const parseTime = (iso: string | null | undefined) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : null;
};

function isLogoutRequest(config: InternalAxiosRequestConfig): boolean {
  const url = (config.url ?? '').split('?')[0];
  return (config.method ?? '').toLowerCase() === 'post' && url === '/auth/logout';
}

/** Why a save waits instead of failing: no network, or S3 not answering while the internet works. */
export type WaitReason = 'offline' | 'unreachable';

export function waitReasonOf(err: unknown): WaitReason | null {
  if (isStorageUnreachable(err)) return 'unreachable';
  return isNetworkFailure(err) ? 'offline' : null;
}

/**
 * `empty`: accepted with no audio (`no_speech`); `lost`: the server found none of the audio this
 * device sent; `partial`: saved from what reached S3 while the device still holds more; `final`:
 * settled without saving; `failed`: retry, on its own when `wait` says so. Only `saved` and `empty`
 * clear this device's copy.
 */
export type SaveResult =
  | { kind: 'saved' }
  | { kind: 'empty' }
  | { kind: 'lost' }
  | { kind: 'partial' }
  | { kind: 'final'; code: string }
  | { kind: 'failed'; wait: WaitReason | null };

/**
 * Drain this device's parts, then complete. A lost upload is re-opened and every part sent again;
 * parts the server reports missing are sent once more; if they still don't match, what reached S3
 * is saved and the device keeps its full copy. The copy is dropped only after a full, accepted save.
 */
export async function saveRecording(
  slug: string,
  dbName: string,
  entry: SessionSidecar,
  recordedSeconds: number | undefined,
  hasLocalParts: boolean,
  inputSwaps?: readonly MicSwapEvent[],
  onUploadReset?: (uploadId: string) => void,
): Promise<SaveResult> {
  try {
    let current = entry;
    const complete = async (withLocal: boolean) => {
      const parts = withLocal ? await drainLocalParts(dbName, slug, current) : [];
      return completeWithFallback(slug, current.recordingId, parts, recordedSeconds, inputSwaps);
    };
    let outcome = await complete(hasLocalParts);
    let partial = false;
    if (outcome.kind === 'gone') {
      const reopened = await reopenUpload(slug, current.recordingId, current.uploadId);
      if (reopened.kind === 'final') return { kind: 'final', code: reopened.code };
      if (reopened.kind === 'reset') {
        current = { ...current, uploadId: reopened.uploadId };
        onUploadReset?.(reopened.uploadId);
        await restartLocalUpload(dbName, current.clientSessionId);
      }
      // `stored`: the audio is already in S3, so finish from it and keep this copy as a safety net.
      partial = reopened.kind === 'stored';
      outcome = await complete(!partial);
    }
    if (outcome.kind === 'missing') {
      await requeueLocalParts(dbName, current.clientSessionId, outcome.parts);
      outcome = await complete(true);
    }
    if (outcome.kind === 'missing' || outcome.kind === 'gone') {
      partial = true;
      outcome = await complete(false);
    }
    if (outcome.kind === 'lost') return { kind: 'lost' };
    if (outcome.kind === 'final') return { kind: 'final', code: outcome.code };
    if (outcome.kind !== 'accepted') return { kind: 'failed', wait: null };
    if (partial) return isEmptyStop(outcome.summary) ? { kind: 'lost' } : { kind: 'partial' };
    if (hasLocalParts) await clearLocalParts(dbName, current.clientSessionId);
    return outcome.summary.status === 'no_speech' ? { kind: 'empty' } : { kind: 'saved' };
  } catch (err) {
    return { kind: 'failed', wait: waitReasonOf(err) };
  }
}

/** Whether a save left this device's copy in place (the session record must then stay). */
export const keepsLocalCopy = (result: SaveResult) =>
  result.kind === 'lost' || result.kind === 'partial' || result.kind === 'final';

/** What the caller hears when a save ends without a full save; `download` offers the device copy. */
export function saveEndedToast(result: SaveResult, download?: () => void): void {
  const action = download ? { label: QM_COPY.download, onClick: download } : undefined;
  if (result.kind === 'empty') toast.info(QM_COPY.noAudio);
  else if (result.kind === 'lost') toast.warning({ title: QM_COPY.audioNotReceived, action, duration: 15_000 });
  else if (result.kind === 'partial') toast.warning({ title: QM_COPY.partialSaved, action, duration: 15_000 });
  else if (result.kind === 'final') toast.error({ title: START_ERROR_COPY[result.code] ?? QM_COPY.cantSave, action });
}

interface Params {
  userId: string | null;
  slug: string | null;
  noticeVersion: string;
  refresh: () => void;
  markUnavailable: (reason: QuickMeetingUnavailableReason) => void;
}

/**
 * This tab's Quick Meeting recorder on `@clarix/meeting-recording`, used unchanged
 * (API-CONTRACT §18.4): mic first, then the server row, then a fresh engine per recording.
 */
export function useQuickMeetingRecorder({ userId, slug, noticeVersion, refresh, markUnavailable }: Params) {
  const scope = userId && slug ? quickMeetingScope(userId, slug) : null;
  const [local, setLocal] = useState<LocalRecording>(IDLE);
  const [engine, setEngine] = useState<EngineMount | null>(null);
  const [signOut, setSignOut] = useState<{ busy: boolean; error: string | null } | null>(null);

  const localRef = useRef(local);
  const sessionRef = useRef<Session | null>(null);
  const controlsRef = useRef<{ key: string; controls: UseRecordingControlsReturn } | null>(null);
  const engineStateRef = useRef<{ key: string; status: ClaraRecordingStatus; error: string | null } | null>(
    null,
  );
  const controlWaitersRef = useRef(new Map<string, (c: UseRecordingControlsReturn) => void>());
  const engineListenersRef = useRef(new Set<() => void>());
  const phaseWaitersRef = useRef(new Set<() => void>());
  const releaseLockRef = useRef<(() => void) | null>(null);
  const signOutResolveRef = useRef<(() => void) | null>(null);
  const recordedHereRef = useRef(new Set<string>());
  const backstopChimeKeyRef = useRef<string | null>(null);
  const stopRef = useRef<(reason: StopReason) => Promise<void>>(async () => {});
  const resumeRef = useRef<() => void>(() => {});
  const retrySaveRef = useRef<() => Promise<boolean>>(async () => false);
  const clockRef = useRef<ClockSample | null>(null);
  /** The clock when this run of capture began (start, resume or a re-base); counting follows its audio clock. */
  const segmentRef = useRef<ClockSample | null>(null);
  /** Consecutive waits for the network; each retry waits twice as long, up to a cap. */
  const waitRetriesRef = useRef(0);
  const paramsRef = useRef({ userId, slug, scope, noticeVersion, refresh, markUnavailable });
  useEffect(() => {
    paramsRef.current = { userId, slug, scope, noticeVersion, refresh, markUnavailable };
  });

  // Every local change goes through here, so callbacks read the current value from the ref.
  const update = useCallback((patch: Partial<LocalRecording>) => {
    const prev = localRef.current;
    const next = { ...prev, ...patch };
    localRef.current = next;
    setLocal(next);
    if (next.phase !== prev.phase) {
      const waiters = [...phaseWaitersRef.current];
      phaseWaitersRef.current.clear();
      waiters.forEach((w) => w());
    }
  }, []);

  const ownState = useCallback((): RemoteRecording | null => {
    const l = localRef.current;
    // A save waiting for the network has ended capture and freed the lock: other tabs need not show it.
    if (!l.recordingId || l.awaitingNetwork) return null;
    if (l.phase !== 'recording' && l.phase !== 'paused' && l.phase !== 'saving') return null;
    return {
      recordingId: l.recordingId,
      phase: l.phase,
      accumulatedMs: l.accumulatedMs,
      runningSince: l.runningSince,
    };
  }, []);

  const onStopRequested = useCallback(() => void stopRef.current('remote'), []);
  const cross = useCrossTabRecording({ scope, getOwnState: ownState, onStopRequested });
  const crossRef = useRef(cross);
  useEffect(() => {
    crossRef.current = cross;
  });

  const announceOwn = useCallback(() => {
    const own = ownState();
    if (own) crossRef.current.announce(own);
  }, [ownState]);

  const releaseLock = useCallback(() => {
    releaseLockRef.current?.();
    releaseLockRef.current = null;
  }, []);

  /**
   * Capture is over but the save is not: free the lock, keep the session for Retry. A failure that was
   * only the network waits instead, and saves on its own once the network answers.
   */
  const toSaveFailed = useCallback(
    (patch: Partial<LocalRecording> = {}, wait: WaitReason | null = null) => {
      const session = sessionRef.current;
      if (session) crossRef.current.announceEnded(session.recordingId);
      releaseLock();
      if (wait || isConnectivityLost()) {
        const uploadStuck = wait === 'unreachable';
        update({ ...patch, phase: 'saving', runningSince: null, awaitingNetwork: true, uploadStuck, error: null });
      } else {
        update({ ...patch, phase: 'save_failed', runningSince: null, error: QM_COPY.saveFailedBody });
      }
    },
    [releaseLock, update],
  );

  /**
   * The recording left this tab's hands: saved, settled or discarded. `keepLocal`: its audio is still
   * on this device, so the session record stays (ended) as the index for "Download from this device".
   */
  const finishSession = useCallback((opts: { keepLocal?: boolean } = {}) => {
    const session = sessionRef.current;
    const { scope: sc } = paramsRef.current;
    if (session) {
      if (sc && opts.keepLocal) sidecars.markEnded(sc, session.recordingId);
      else if (sc) sidecars.remove(sc, session.recordingId);
      crossRef.current.announceEnded(session.recordingId);
    }
    waitRetriesRef.current = 0;
    sessionRef.current = null;
    releaseLock();
    update({ ...IDLE, endedId: session?.recordingId ?? null });
    paramsRef.current.refresh();
  }, [releaseLock, update]);

  /** The server can't match the parts: keep the session so the pill can offer Discard. */
  const toNoAudio = useCallback(() => {
    const session = sessionRef.current;
    if (session) crossRef.current.announceEnded(session.recordingId);
    releaseLock();
    update({ phase: 'no_audio', runningSince: null, error: null });
  }, [releaseLock, update]);

  const handleComplete = useCallback(
    (recordingId: string, outcome: CompleteOutcome) => {
      if (sessionRef.current?.recordingId !== recordingId) return;
      if (outcome.kind === 'final' && outcome.code === NO_AUDIO_CODE) {
        toNoAudio();
        return;
      }
      if (outcome.kind === 'missing' || outcome.kind === 'gone') {
        // The parts stay in IndexedDB (the engine saw no acceptance); Retry re-opens or re-sends them.
        toSaveFailed();
        void retrySaveRef.current();
        return;
      }
      if (outcome.kind === 'accepted') {
        recordedHereRef.current.add(recordingId);
        finishSession();
        return;
      }
      // Not accepted, so the engine kept every part: keep their index too and offer them.
      const download = downloadFor(paramsRef.current.scope, sessionRef.current);
      saveEndedToast(outcome.kind === 'lost' ? { kind: 'lost' } : { kind: 'final', code: outcome.code }, download);
      finishSession({ keepLocal: true });
    },
    [finishSession, toNoAudio, toSaveFailed],
  );

  const handleCompleteFailed = useCallback(
    (recordingId: string, error: unknown) => {
      if (sessionRef.current?.recordingId === recordingId) toSaveFailed({}, waitReasonOf(error));
    },
    [toSaveFailed],
  );

  // ── Sleep: the audio clock stops while the computer sleeps, the wall clock does not ─────

  const sampleClock = useCallback((): ClockSample => {
    const c = controlsRef.current;
    const live = c && c.key === sessionRef.current?.key ? c.controls : null;
    return { wall: Date.now(), audio: live?.getAnalyser()?.context.currentTime ?? null };
  }, []);

  /** The time to count recording up to: as far as the graph rendered this run, else where capture froze. */
  const countedAt = useCallback((next: ClockSample, prev: ClockSample | null): number => {
    const segment = segmentRef.current;
    return (segment ? capturedUntil(segment, next) : null) ?? (prev ? freezeStart(prev, next) : null) ?? next.wall;
  }, []);

  const countedNow = useCallback(() => {
    const next = sampleClock();
    const prev = clockRef.current;
    if (!prev || localRef.current.phase !== 'recording') return next.wall;
    return countedAt(next, prev);
  }, [countedAt, sampleClock]);

  // ── Engine bridge (see EngineBridge.tsx) ─────────────────────────────────────────────

  const onControls = useCallback((key: string, controls: UseRecordingControlsReturn) => {
    controlsRef.current = { key, controls };
    const waiter = controlWaitersRef.current.get(key);
    if (waiter) {
      controlWaitersRef.current.delete(key);
      waiter(controls);
    }
  }, []);

  const onEngineState = useCallback(
    (key: string, status: ClaraRecordingStatus, error: string | null) => {
      engineStateRef.current = { key, status, error };
      [...engineListenersRef.current].forEach((l) => l());
      if (backstopChimeKeyRef.current === key && status !== 'completing') {
        backstopChimeKeyRef.current = null;
        void playChime('stop');
      }
      const session = sessionRef.current;
      if (!session || session.key !== key) return;
      const cur = localRef.current;
      const live = cur.phase === 'recording' || cur.phase === 'paused';
      if (status === 'completing' && live) {
        // The engine stopped on its own (its backstop, or a silent mic); it captures until it leaves 'completing'.
        backstopChimeKeyRef.current = key;
        update({ phase: 'saving', accumulatedMs: recordedMsAt(cur, countedNow()), runningSince: null, pauseReason: null });
        announceOwn();
      } else if (status === 'error' && error === SAVE_REFUSED_MESSAGE) {
        // The adapter already reported that answer to this hook (handleComplete); nothing more to do.
      } else if (status === 'error' && cur.phase === 'saving' && !cur.awaitingNetwork) {
        toSaveFailed({}, engineWaitReason(error));
      } else if (status === 'error' && live) {
        // Never abort here: that would wipe IndexedDB. The parts stay for Retry.
        toSaveFailed({ accumulatedMs: recordedMsAt(cur, countedNow()) }, engineWaitReason(error));
      }
    },
    [announceOwn, countedNow, toSaveFailed, update],
  );

  const mountEngine = useCallback(
    (mount: EngineMount) =>
      new Promise<UseRecordingControlsReturn>((resolve, reject) => {
        const timer = window.setTimeout(() => {
          controlWaitersRef.current.delete(mount.key);
          reject(new Error('engine did not mount'));
        }, QM_TIMEOUTS.ENGINE_SETTLE_MS);
        controlWaitersRef.current.set(mount.key, (controls) => {
          window.clearTimeout(timer);
          resolve(controls);
        });
        setEngine(mount);
      }),
    [],
  );

  const waitForEngine = useCallback(
    (key: string, accept: (s: ClaraRecordingStatus) => boolean) =>
      new Promise<ClaraRecordingStatus | null>((resolve) => {
        let timer = 0;
        const check = () => {
          const s = engineStateRef.current;
          if (s?.key !== key || !accept(s.status)) return;
          engineListenersRef.current.delete(check);
          window.clearTimeout(timer);
          resolve(s.status);
        };
        engineListenersRef.current.add(check);
        timer = window.setTimeout(() => {
          engineListenersRef.current.delete(check);
          resolve(null);
        }, QM_TIMEOUTS.ENGINE_SETTLE_MS);
        check();
      }),
    [],
  );

  // ── Actions ──────────────────────────────────────────────────────────────────────────

  /**
   * Discards this tab's take: the server row, then this device's copy. Unless `force` (a too-short take),
   * a row the server keeps keeps the session too, so nothing local is dropped for a row that still exists.
   */
  const discardSession = useCallback(
    async (force = false): Promise<boolean> => {
      const session = sessionRef.current;
      const { slug: sl, scope: sc } = paramsRef.current;
      if (!session || !sl) return false;
      const controls = controlsRef.current;
      if (controls?.key === session.key) await controls.controls.abort().catch(() => undefined);
      const result = await abortWithRetry(sl, session.recordingId);
      if (!result.ok && !force) {
        toast.error(START_ERROR_COPY[result.code] ?? QM_COPY.actionFailed);
        return false;
      }
      if (result.ok && sc) await clearLocalParts(sc, session.key);
      finishSession();
      return true;
    },
    [finishSession],
  );

  const start = useCallback(
    async ({ title }: StartInput) => {
      const { userId: uid, slug: sl, scope: sc, noticeVersion: version } = paramsRef.current;
      const phase = localRef.current.phase;
      if (!sl || !sc || (phase !== 'idle' && phase !== 'denied')) return;
      // Read once: the probe and the engine must open the same choice.
      const chosenMic = savedMic.get(uid);
      primeChime();
      update({ ...IDLE, phase: 'requesting_mic' });

      const release = await acquireRecordingLock(sc);
      if (!release) {
        update({ ...IDLE, error: ANOTHER_TAB_START });
        return;
      }
      releaseLockRef.current = release;

      const mic = await probeMicrophone(chosenMic);
      if (mic !== 'granted') {
        releaseLock();
        const error = mic === 'unsupported' ? MIC_UNSUPPORTED : mic === 'no_device' ? MIC_NO_DEVICE : null;
        update({ ...IDLE, phase: mic === 'denied' ? 'denied' : 'idle', error });
        return;
      }

      update({ phase: 'starting' });
      const clientSessionId = uuid();
      const body = {
        ...(title.trim() ? { title: title.trim().slice(0, QM_LIMITS.TITLE_MAX) } : {}),
        client_session_id: clientSessionId,
        // Pressing Start is the attestation (the form says so); there is no outside-guests question.
        notice_attested: true as const,
        notice_version: version,
        legal_basis: 'employment' as const,
      };
      let created: CreateQuickMeetingResponse;
      try {
        // Idempotent on client_session_id: a lost response is replayed, never duplicated.
        created = await quickMeetingApi.create(sl, body).catch((err: unknown) => {
          const { status, code } = errorCode(err);
          // STORAGE_NOT_CONFIGURED is a 503 that answers rather than an outage; replaying only delays it.
          if ((status === 0 || status >= 500) && code !== 'STORAGE_NOT_CONFIGURED') {
            return quickMeetingApi.create(sl, body);
          }
          throw err;
        });
      } catch (err) {
        releaseLock();
        // No storage swaps the form for the panel's notice, so a red error would only flash or go stale.
        const noStorage = errorCode(err).code === 'STORAGE_NOT_CONFIGURED';
        update({ ...IDLE, error: noStorage ? null : startErrorMessage(err) });
        if (noStorage) paramsRef.current.markUnavailable('STORAGE_NOT_CONFIGURED');
        paramsRef.current.refresh();
        return;
      }

      const { recording, upload, event } = created;
      const adapter = createCalendarRecordingAdapter({
        slug: sl,
        recordingId: recording.id,
        upload,
        recordedSeconds: () => Math.round(recordedMsAt(localRef.current, Date.now()) / 1000),
        onComplete: (outcome) => handleComplete(recording.id, outcome),
        onCompleteFailed: (err) => handleCompleteFailed(recording.id, err),
      });
      const entry = {
        recordingId: recording.id,
        clientSessionId,
        uploadId: upload.upload_id,
        title: recording.title,
        startedAt: recording.started_at,
      };
      sessionRef.current = { ...entry, key: clientSessionId, adapter };
      sidecars.add(sc, entry);

      let settled: ClaraRecordingStatus | null = null;
      try {
        const mounted = await mountEngine({ key: clientSessionId, adapter, dbName: sc });
        // The cue must die away before the engine opens the mic, or it lands in the recording.
        await playChime('start');
        const latest = controlsRef.current;
        const controls = latest?.key === clientSessionId ? latest.controls : mounted;
        const outcome = waitForEngine(clientSessionId, (s) => s === 'recording' || s === 'error');
        await controls.start(clientSessionId, `quick-meeting-${clientSessionId}`, { mic: chosenMic });
        settled = await outcome;
      } catch {
        settled = null;
      }

      if (settled !== 'recording') {
        // The engine failed after the row exists and never aborts it itself (§18.4); it has
        // already toasted its own reason, so the shell adds none.
        if (controlsRef.current?.key === clientSessionId) {
          await controlsRef.current.controls.abort().catch(() => undefined);
        }
        // That abort wiped the take's IndexedDB, so no Save may be offered; a row the server keeps
        // is still reachable from the popover, with Discard only.
        sidecars.remove(sc, recording.id);
        await abortWithRetry(sl, recording.id);
        sessionRef.current = null;
        releaseLock();
        update({ ...IDLE });
        paramsRef.current.refresh();
        return;
      }

      const now = Date.now();
      update({
        ...IDLE,
        phase: 'recording',
        recordingId: recording.id,
        eventId: event.id,
        stopAt: parseTime(upload.stop_at),
        warnAt: parseTime(upload.warn_at),
        runningSince: now,
      });
      announceOwn();
      paramsRef.current.refresh();
    },
    [announceOwn, handleComplete, handleCompleteFailed, mountEngine, releaseLock, update, waitForEngine],
  );

  const pause = useCallback(() => {
    const cur = localRef.current;
    if (cur.phase !== 'recording') return;
    controlsRef.current?.controls.pause();
    update({ phase: 'paused', accumulatedMs: recordedMsAt(cur, countedNow()), runningSince: null, pauseReason: null });
    announceOwn();
  }, [announceOwn, countedNow, update]);

  const resume = useCallback(() => {
    if (localRef.current.phase !== 'paused') return;
    toast.dismiss(SLEEP_TOAST_ID);
    controlsRef.current?.controls.resume();
    update({ phase: 'recording', runningSince: Date.now(), pauseReason: null });
    announceOwn();
  }, [announceOwn, update]);
  useEffect(() => {
    resumeRef.current = resume;
  }, [resume]);

  /** The computer slept mid-take: pause where capture froze, so the time away is never counted. */
  const pauseForSleep = useCallback(
    (frozeAt: number) => {
      const cur = localRef.current;
      if (cur.phase !== 'recording') return;
      controlsRef.current?.controls.pause();
      const until = Math.max(frozeAt, cur.runningSince ?? frozeAt);
      update({ phase: 'paused', accumulatedMs: recordedMsAt(cur, until), runningSince: null, pauseReason: 'asleep' });
      announceOwn();
      toast.warning({
        id: SLEEP_TOAST_ID,
        title: QM_COPY.asleepPaused,
        duration: 15_000,
        action: { label: QM_COPY.resume, onClick: () => resumeRef.current() },
      });
    },
    [announceOwn, update],
  );

  const stop = useCallback(
    async (reason: StopReason) => {
      const cur = localRef.current;
      if (cur.phase !== 'recording' && cur.phase !== 'paused') return;
      const session = sessionRef.current;
      const controls = controlsRef.current;
      if (!session || controls?.key !== session.key) return;
      const recordedMs = recordedMsAt(cur, countedNow());
      if (recordedMs < QM_LIMITS.MIN_RECORDING_MS) {
        await discardSession(true);
        toast.info(QM_COPY.tooShort);
        return;
      }
      // Pausing halts capture at once; the engine's stop keeps capturing through its flush.
      if (cur.phase === 'recording') controls.controls.pause();
      window.setTimeout(() => void playChime('stop'), QM_TIMEOUTS.STOP_CHIME_GUARD_MS);
      update({ phase: 'saving', accumulatedMs: recordedMs, runningSince: null, pauseReason: null });
      toast.dismiss(SLEEP_TOAST_ID);
      announceOwn();
      if (reason === 'auto') toast.info(QM_COPY.autoStopped);
      await controls.controls.stop();
    },
    [announceOwn, countedNow, discardSession, update],
  );
  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);

  /** Upload what this tab still holds, then complete (the "Couldn't save: Retry" path). */
  const retrySave = useCallback(async (): Promise<boolean> => {
    const session = sessionRef.current;
    const { slug: sl, scope: sc } = paramsRef.current;
    const cur = localRef.current;
    if (!session || !sl || !sc || (cur.phase !== 'save_failed' && !cur.awaitingNetwork)) return false;
    update({ phase: 'saving', error: null, awaitingNetwork: false, uploadStuck: false });
    const seconds = Math.round(cur.accumulatedMs / 1000);
    const result = await saveRecording(sl, sc, session, seconds, true, session.adapter.inputSwaps, (uploadId) => {
      session.uploadId = uploadId;
      sidecars.update(sc, session.recordingId, { uploadId });
    });
    if (result.kind === 'failed') {
      toSaveFailed({}, result.wait);
      return false;
    }
    if (result.kind === 'final' && result.code === NO_AUDIO_CODE) {
      toNoAudio();
      return true;
    }
    if (result.kind === 'saved') {
      recordedHereRef.current.add(session.recordingId);
      toast.success({ title: QM_COPY.savedTitle, description: QM_COPY.savedBody });
    } else {
      if (result.kind === 'partial') recordedHereRef.current.add(session.recordingId);
      saveEndedToast(result, downloadFor(sc, session));
    }
    finishSession({ keepLocal: keepsLocalCopy(result) });
    return true;
  }, [finishSession, toNoAudio, toSaveFailed, update]);
  useEffect(() => {
    retrySaveRef.current = retrySave;
  }, [retrySave]);

  /** The row moved on elsewhere (saved or discarded): stop capturing, and keep this device's audio. */
  const endedElsewhere = useCallback(
    (message: string) => {
      const session = sessionRef.current;
      if (!session) return;
      if (controlsRef.current?.key === session.key) void controlsRef.current.controls.halt();
      finishSession({ keepLocal: true });
      toast.info({ title: message, action: downloadAction(downloadFor(paramsRef.current.scope, session)), duration: 15_000 });
    },
    [finishSession],
  );

  const sendHeartbeat = useCallback(async () => {
    const session = sessionRef.current;
    const { slug: sl } = paramsRef.current;
    const cur = localRef.current;
    if (!session || !sl) return;
    try {
      const res = await quickMeetingApi.heartbeat(sl, session.recordingId, {
        recorded_seconds: Math.round(recordedMsAt(cur, countedNow()) / 1000),
        paused: cur.phase === 'paused',
      });
      if (sessionRef.current !== session) return;
      if (res.status === 'discarded') {
        endedElsewhere(QM_COPY.endedElsewhere);
        return;
      }
      const stopAt = parseTime(res.stop_at) ?? localRef.current.stopAt;
      const warnAt = parseTime(res.warn_at) ?? localRef.current.warnAt;
      if (stopAt !== localRef.current.stopAt || warnAt !== localRef.current.warnAt) update({ stopAt, warnAt });
    } catch (err) {
      // A network failure is ignored: the take keeps recording and the server marks the row paused meanwhile.
      const { status, code, details } = errorCode(err);
      const phase = localRef.current.phase;
      if (sessionRef.current !== session || (phase !== 'recording' && phase !== 'paused')) return;
      const rowStatus = code === 'INVALID_RECORDING_STATE' ? (details as { status?: unknown } | undefined)?.status : null;
      if (status === 404) {
        // A 404 could be a passing proxy fault: only a second read that agrees ends the take.
        const now = await rowNow(sl, session.recordingId);
        if (sessionRef.current !== session) return;
        if (now === 'gone') endedElsewhere(QM_COPY.endedElsewhere);
        else if (now && !STILL_UPLOADING.has(now)) endedElsewhere(QM_COPY.savedElsewhere);
      } else if (rowStatus === 'discarded') endedElsewhere(QM_COPY.endedElsewhere);
      else if (typeof rowStatus === 'string' && !STILL_UPLOADING.has(rowStatus)) endedElsewhere(QM_COPY.savedElsewhere);
    }
  }, [countedNow, endedElsewhere, update]);

  // ── Effects ──────────────────────────────────────────────────────────────────────────

  const { phase, stopAt, warnAt, warned } = local;
  const heartbeatOn = phase === 'recording' || phase === 'paused' || phase === 'saving';
  useEffect(() => {
    if (!heartbeatOn) return;
    const id = window.setInterval(() => void sendHeartbeat(), QM_LIMITS.HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [heartbeatOn, sendHeartbeat]);

  // Warn at `warn_at`; stop at the wall-clock `stop_at`, pauses included (§18.1 D6).
  useEffect(() => {
    if (phase !== 'recording' && phase !== 'paused') return;
    const timers: number[] = [];
    if (warnAt != null && !warned) {
      timers.push(
        window.setTimeout(() => {
          update({ warned: true });
          toast.warning({ title: QM_COPY.autoStopSoonTitle, description: QM_COPY.autoStopSoonBody, duration: 10_000 });
        }, Math.max(0, warnAt - Date.now())),
      );
    }
    if (stopAt != null) {
      timers.push(window.setTimeout(() => void stopRef.current('auto'), Math.max(0, stopAt - Date.now())));
    }
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [phase, stopAt, warnAt, warned, update]);

  useEffect(() => {
    if (phase !== 'recording') {
      clockRef.current = null;
      segmentRef.current = null;
      return;
    }
    clockRef.current = sampleClock();
    segmentRef.current = clockRef.current;
    const id = window.setInterval(() => {
      const prev = clockRef.current;
      const next = sampleClock();
      clockRef.current = next;
      if (prev && freezeStart(prev, next) != null) {
        pauseForSleep(countedAt(next, prev));
        return;
      }
      // The graph fell behind the wall clock: re-base, so the pill and heartbeat show only captured audio.
      const cur = localRef.current;
      const counted = countedAt(next, prev);
      if (cur.phase !== 'recording' || next.wall - counted < QM_RECOVERY.AUDIO_LAG_MS) return;
      segmentRef.current = next;
      update({ accumulatedMs: recordedMsAt(cur, counted), runningSince: next.wall });
      announceOwn();
    }, QM_RECOVERY.SLEEP_CHECK_MS);
    return () => window.clearInterval(id);
  }, [phase, announceOwn, countedAt, pauseForSleep, sampleClock, update]);

  // Capture carries on offline: the parts wait in IndexedDB and upload once the network is back.
  const capturing = phase === 'recording' || phase === 'paused';
  useEffect(() => {
    if (!capturing) return;
    const onOffline = () => toast.warning({ id: OFFLINE_TOAST_ID, title: QM_COPY.offlineRecording, duration: 10_000 });
    const onOnline = () => toast.dismiss(OFFLINE_TOAST_ID);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    };
  }, [capturing]);

  // A save waiting for the network retries on a timer, and at once when the connection comes back.
  const { awaitingNetwork } = local;
  useEffect(() => {
    if (!awaitingNetwork) return;
    let done = false;
    const retry = () => {
      if (done) return;
      done = true;
      void retrySaveRef.current();
    };
    const delay = Math.min(QM_RECOVERY.NETWORK_RETRY_MAX_MS, QM_RECOVERY.NETWORK_RETRY_MS * 2 ** waitRetriesRef.current);
    waitRetriesRef.current += 1;
    const timer = window.setTimeout(retry, delay);
    const onBack = () => {
      if (!isConnectivityLost()) retry();
    };
    window.addEventListener('online', onBack);
    const unsubscribe = subscribeConnectivity(onBack);
    return () => {
      done = true;
      window.clearTimeout(timer);
      window.removeEventListener('online', onBack);
      unsubscribe();
    };
  }, [awaitingNetwork]);

  // RecordingProvider's visibilitychange handler resumes a paused worklet; window listeners
  // run after its document one, so pause again (§18.4).
  useEffect(() => {
    if (phase !== 'paused') return;
    const repause = () => {
      if (document.visibilityState !== 'visible') return;
      const controls = controlsRef.current?.controls;
      controls?.resume();
      controls?.pause();
    };
    window.addEventListener('visibilitychange', repause);
    return () => window.removeEventListener('visibilitychange', repause);
  }, [phase]);

  // The engine guards recording/paused/completing itself; these two are the shell's own.
  useEffect(() => {
    if (phase !== 'starting' && phase !== 'saving') return;
    const guard = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [phase]);

  // Leaving the workspace shell stops the engine, so the mic is never live without an indicator.
  const teardown = useCallback(() => {
    const p = localRef.current.phase;
    if (p === 'recording' || p === 'paused') void controlsRef.current?.controls.stop();
    releaseLock();
  }, [releaseLock]);
  useEffect(() => teardown, [teardown]);

  // ── Sign-out guard: hold POST /auth/logout until the recording is saved (§18.10) ─────

  const waitForPhaseChange = useCallback(
    (timeoutMs: number) =>
      new Promise<void>((resolve) => {
        const timer = window.setTimeout(done, Math.max(0, timeoutMs));
        function done() {
          window.clearTimeout(timer);
          phaseWaitersRef.current.delete(done);
          resolve();
        }
        phaseWaitersRef.current.add(done);
      }),
    [],
  );

  const prepareForSignOut = useCallback(async () => {
    if (crossRef.current.remote) {
      crossRef.current.requestRemoteStop();
      const until = Date.now() + QM_TIMEOUTS.SIGN_OUT_REMOTE_MS;
      while (crossRef.current.remote && Date.now() < until) {
        await new Promise((r) => window.setTimeout(r, 500));
      }
    }
    const deadline = Date.now() + QM_TIMEOUTS.SIGN_OUT_SAVE_MS;
    while (Date.now() < deadline) {
      const p = localRef.current.phase;
      if (p === 'idle' || p === 'denied' || p === 'no_audio') return;
      if (p === 'save_failed' || localRef.current.awaitingNetwork) {
        await new Promise<void>((resolve) => {
          signOutResolveRef.current = resolve;
          setSignOut({ busy: false, error: null });
        });
        return;
      }
      if (p === 'recording' || p === 'paused') {
        await stopRef.current('signout');
        if (localRef.current.phase === p) return;
      } else {
        await waitForPhaseChange(deadline - Date.now());
      }
    }
  }, [waitForPhaseChange]);

  const prepareRef = useRef(prepareForSignOut);
  useEffect(() => {
    prepareRef.current = prepareForSignOut;
  }, [prepareForSignOut]);

  useEffect(() => {
    let client: ReturnType<typeof getApiClient>;
    try {
      client = getApiClient();
    } catch {
      return;
    }
    const id = client.interceptors.request.use(async (config) => {
      if (isLogoutRequest(config)) await prepareRef.current().catch(() => undefined);
      return config;
    });
    return () => client.interceptors.request.eject(id);
  }, []);

  const closeSignOut = useCallback(() => {
    setSignOut(null);
    signOutResolveRef.current?.();
    signOutResolveRef.current = null;
  }, []);

  const signOutUpload = useCallback(async () => {
    setSignOut({ busy: true, error: null });
    const ok = await retrySave();
    if (ok) closeSignOut();
    else setSignOut({ busy: false, error: QM_COPY.saveFailedBody });
  }, [closeSignOut, retrySave]);

  const signOutDeleteLocal = useCallback(async () => {
    const session = sessionRef.current;
    const { scope: sc } = paramsRef.current;
    // Only this device's copy: what reached S3 stays with the paused recording, to save or discard later.
    if (session && sc) await clearLocalParts(sc, session.key);
    finishSession();
    closeSignOut();
  }, [closeSignOut, finishSession]);

  // Bound to this session's engine: a finished take's provider stays mounted until the next start.
  const engineMic = useMemo<EngineMic>(() => {
    const current = () => {
      const c = controlsRef.current;
      return c && c.key === sessionRef.current?.key ? c.controls : null;
    };
    return {
      getActiveMic: () => current()?.getActiveMic() ?? null,
      getAnalyser: () => current()?.getAnalyser() ?? null,
      switchMic: (choice, reason) =>
        current()?.switchMic(choice, reason) ?? Promise.reject(new Error('Nothing is being recorded')),
    };
  }, []);

  const takeRecordedHere = useCallback((id: string) => recordedHereRef.current.delete(id), []);
  const clearError = useCallback(() => {
    const p = localRef.current.phase;
    update({ error: null, ...(p === 'denied' ? { phase: 'idle' as const } : {}) });
  }, [update]);

  return {
    local,
    remote: cross.remote,
    engine,
    engineMic,
    bridge: { onControls, onEngineState },
    actions: {
      start,
      pause,
      resume,
      stop: () => void stop('user'),
      retrySave,
      discardOwn: () => discardSession(),
      downloadOwn: () => downloadFor(paramsRef.current.scope, sessionRef.current)?.(),
      stopRemote: cross.requestRemoteStop,
      clearError,
    },
    signOut: signOut && {
      ...signOut,
      upload: () => void signOutUpload(),
      deleteLocal: () => void signOutDeleteLocal(),
    },
    takeRecordedHere,
  };
}
